import React, { useCallback, useEffect, useState } from 'react';
import Overview from './components/Overview';
import QuickNavigation from './components/QuickNavigation';
import AlertPlateCard from './components/AlertPlate';
import { HomePageInfo } from './types';
import { getDeviceMessage, getHomePageInfo, getCurrent } from '@/api';
import './index.less';
import { Row, Col, Card, Typography } from 'antd';
import TimeDatePicker from '@/components/TimePicker';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { Area, G2, Gauge } from '@ant-design/plots';

const { Title } = Typography;

type DashboardProps = {};

function SingleGaugeChart({ title, value, description }: { title: string; value: number; description: string }) {
  const { registerShape, Util } = G2; // 自定义 Shape 部分

  registerShape('point', 'custom-gauge-indicator', {
    draw(cfg, container) {
      // 使用 customInfo 传递参数
      const { indicator, defaultColor } = cfg.customInfo;
      const { pointer } = indicator;
      const group = container.addGroup(); // 获取极坐标系下画布中心点

      const center = (this as any).parsePoint({
        x: 0,
        y: 0,
      }); // 绘制指针

      if (pointer) {
        const { startAngle, endAngle } = Util.getAngle(cfg, (this as any).coordinate);
        const radius = (this as any).coordinate.getRadius();
        const midAngle = (startAngle + endAngle) / 2;
        const { x: x1, y: y1 } = Util.polarToCartesian(center.x, center.y, radius / 15, midAngle + 1 / Math.PI);
        const { x: x2, y: y2 } = Util.polarToCartesian(center.x, center.y, radius / 15, midAngle - 1 / Math.PI);
        const { x, y } = Util.polarToCartesian(center.x, center.y, radius * 0.65, midAngle);
        const path = [['M', center.x, center.y], ['L', x1, y1], ['L', x, y], ['L', x2, y2], ['Z']]; // pointer

        group.addShape('path', {
          name: 'pointer',
          attrs: {
            path,
            fill: defaultColor,
            ...pointer.style,
          },
        });
      }

      return group;
    },
  });
  const config = {
    percent: value / 100,
    range: {
      ticks: [0, 0.3, 0.6, 1],
      color: ['green', '#FAAD14', '#F4664A'],
    },
    indicator: {
      shape: 'custom-gauge-indicator',
      pointer: {
        style: {
          stroke: value > 60 ? '#F4664A' : value > 30 ? '#FAAD14' : 'green',
          lineWidth: 1,
          fill: value > 60 ? '#F4664A' : value > 30 ? '#FAAD14' : 'green',
        },
      },
    },
    // indicator: {
    //   pointer: {
    //     style: {
    //       stroke: value > 60 ? '#F4664A' : value > 30 ? '#FAAD14' : 'green',
    //     },
    //   },
    //   pin: {
    //     style: {
    //       stroke: value > 60 ? '#F4664A' : value > 30 ? '#FAAD14' : 'green',
    //     },
    //   },
    // },
    statistic: {
      content: {
        formatter: () => `${title}\n${value.toFixed(2)}%`,
        style: {
          fontSize: '12px',
        },
      },
    },
    gaugeStyle: {
      lineCap: 'round' as const,
    },
  };

  return (
    <div>
      <div style={{ width: '100%', height: '180px' }}>
        <Gauge {...config} />
      </div>
      <div style={{ textAlign: 'center', color: '#aaa' }}>{description}</div>
    </div>
  );
}

function MessageLine() {
  const { data, loading, run } = useRequest(getDeviceMessage, { defaultParams: [{ metricsType: '1h', startTime: dayjs().subtract(1, 'hour').valueOf(), endTime: dayjs().valueOf() }] });
  return (
    <Card
      // title="设备消息总数"
      style={{ marginTop: 20 }}
      title={
        <Title style={{ marginTop: '20px' }} level={3}>
          设备消息总数
        </Title>
      }
      loading={loading}
      extra={
        <TimeDatePicker
          onChange={(e) => {
            run(e);
          }}
        />
      }
    >
      <Area
        data={data?.result || []}
        height={200}
        tooltip={{
          formatter: (datum) => {
            return { name: '数据', value: datum.count };
          },
        }}
        {...{
          xField: 'time',
          yField: 'count',
          smooth: true,
          // label: {
          //   offsetY: -6,
          //   style: {
          //     fill: 'rgb(3, 104, 219)',
          //   },
          // },
          point: {
            size: 3,
            shape: 'circle',
            style: {
              fill: 'white',
              stroke: '#5B8FF9',
              lineWidth: 2,
            },
          },
        }}
        style={{ marginTop: '-10px' }}
      />
    </Card>
  );
}

function StatusLine() {
  const { data } = useRequest(getCurrent, { pollingInterval: 3000 });
  return (
    <Card
      // title="状态"
      title={
        <Title style={{ marginTop: '20px' }} level={3}>
          状态
        </Title>
      }
    >
      {/* <div>负载率：{data?.result.loadInfo}</div>
      <div>CPU使用率：{data?.result.cpuUsedPercent}%</div>
      <div>内存使用率：{data?.result.memoryUsedPercent}%</div>
      <div>磁盘使用率：{data?.result.diskUsedPercent}%</div> */}
      <Row gutter={[20, 20]}>
        <Col span={6}>
          <SingleGaugeChart title="CPU" value={data?.result.cpuUsedPercent || 0} description={`(${data?.result.cpuUsed} / ${data?.result.cpuTotal}) 核`} />
        </Col>
        <Col span={6}>
          <SingleGaugeChart title="内存" value={data?.result.memoryUsedPercent || 0} description={`${data?.result.memoryUsed} / ${data?.result.memoryTotal}`} />
        </Col>
        <Col span={6}>
          <SingleGaugeChart title="负载" value={data?.result.loadUsagePercent || 0} description={`${data?.result.loadInfo}`} />
        </Col>
        <Col span={6}>
          <SingleGaugeChart title="磁盘" value={data?.result.diskUsedPercent || 0} description={`${data?.result.diskUsed} / ${data?.result.diskTotal}`} />
        </Col>
      </Row>
    </Card>
  );
}

const Dashboard: React.FC<DashboardProps> = () => {
  const [homePageInfo, setHomePageInfo] = useState<HomePageInfo>();

  const loadHomePageInfo = useCallback(() => {
    getHomePageInfo<HomePageInfo>().then((resp) => {
      if (resp.success) {
        setHomePageInfo({
          ...resp.result,
          // msg_gather: resp.result.msg_gather.reverse(),
        });
      }
    });
  }, []);

  useEffect(() => {
    loadHomePageInfo();
  }, []);

  return (
    <div className="dashboard">
      <Row gutter={[20, 20]}>
        <Col flex={'auto'}>
          <Overview pageInfo={homePageInfo?.pageInfo} />
        </Col>
        <Col flex="none">
          <QuickNavigation quickNavigationList={homePageInfo?.quickNavigation} />
        </Col>
      </Row>

      <Row gutter={[20, 20]} style={{ marginTop: 20 }}>
        <Col flex={'auto'}>
          <div>
            <StatusLine />
          </div>
        </Col>
        <Col flex="none">
          <AlertPlateCard alertPlateList={homePageInfo?.alertPlate || []} />
        </Col>
      </Row>
      <MessageLine />
    </div>
  );
};

export default Dashboard;
